import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  Typography,
  Alert,
  Spin,
  AutoComplete,
  Row,
  Col,
  Grid,
} from "antd";
import {
  UserOutlined,
  MobileOutlined,
  NumberOutlined,
  CheckCircleTwoTone,
  WhatsAppOutlined,
  SafetyCertificateOutlined,
  IdcardOutlined,
} from "@ant-design/icons";
import "../../config";
import axios from "axios";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const GOLD = "#b8860b";
const GOLD_LIGHT = "#d4a537";

// Public, unauthenticated page - shared out-of-band (WhatsApp/QR/social) with
// people who aren't existing customers yet, so they can request temporary
// catalogue access. No login/OTP is required to submit this form -
// see src/components/Login/Login.js for the pincode/state/city lookup this
// mirrors, and Controllers/PublicDataController.cs -> RequestCatalogueAccess
// on the backend. Mobile-first: most visitors land here from a WhatsApp link
// on their phone. Note: user-facing copy deliberately never mentions the
// access duration (business decision) - only admins see the 24h countdown.
const RequestCatalogueAccess = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [name, setName] = useState("");
  const [contactNo, setContactNo] = useState("");
  const [pincode, setPincode] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [gstin, setGstin] = useState("");
  const [indianStates, setIndianStates] = useState([]);
  const [filteredStates, setFilteredStates] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.title = "JOJ | Get Catalogue Access";

    async function fetchStates() {
      try {
        const response = await fetch(
          "https://countriesnow.space/api/v0.1/countries/states",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ country: "India" }),
          }
        );
        const data = await response.json();
        if (!data.error && data.data && Array.isArray(data.data.states)) {
          const stateNames = data.data.states.map((s) => s.name);
          setIndianStates(stateNames);
          setFilteredStates(stateNames);
        }
      } catch (err) {
        const fallback = [
          "Maharashtra",
          "Gujarat",
          "Delhi",
          "Karnataka",
          "Tamil Nadu",
          "Uttar Pradesh",
          "West Bengal",
          "Rajasthan",
          "Madhya Pradesh",
          "Punjab",
        ];
        setIndianStates(fallback);
        setFilteredStates(fallback);
      }
    }
    fetchStates();
  }, []);

  const handleStateSearch = (value) => {
    setState(value);
    setFilteredStates(
      indianStates.filter((s) => s.toLowerCase().includes(value.toLowerCase()))
    );
    setFilteredCities([]);
    setCity("");
  };

  const handleStateSelect = async (value) => {
    setState(value);
    setCity("");
    setFilteredCities([]);
    setAllCities([]);
    try {
      const response = await fetch(
        "https://countriesnow.space/api/v0.1/countries/state/cities",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ country: "India", state: value }),
        }
      );
      const data = await response.json();
      if (!data.error && Array.isArray(data.data)) {
        setAllCities(data.data);
        setFilteredCities(data.data);
      }
    } catch (err) {
      setAllCities([]);
      setFilteredCities([]);
    }
  };

  const handleCitySearch = (value) => {
    setCity(value);
    if (allCities && Array.isArray(allCities)) {
      setFilteredCities(
        allCities.filter((c) => c.toLowerCase().includes(value.toLowerCase()))
      );
    } else {
      setFilteredCities([]);
    }
  };

  const handleCitySelect = (value) => {
    setCity(value);
    setFilteredCities(allCities);
  };

  const handlePincodeChange = async (e) => {
    const value = e.target.value.replace(/\D/g, "");
    setPincode(value);

    if (value.length === 6) {
      setPincodeLoading(true);
      try {
        const response = await fetch(
          `https://api.postalpincode.in/pincode/${value}`
        );
        const data = await response.json();
        if (
          Array.isArray(data) &&
          data[0].Status === "Success" &&
          Array.isArray(data[0].PostOffice) &&
          data[0].PostOffice.length > 0
        ) {
          const post = data[0].PostOffice[0];
          setState(post.State);
          setCity(post.Region);
          if (indianStates.includes(post.State)) {
            setFilteredStates([post.State]);
          }
          setFilteredCities([post.Region]);
        }
      } catch (err) {
        // Optionally handle error
      } finally {
        setPincodeLoading(false);
      }
    }
  };

  const handleSubmit = async () => {
    setError("");
    if (!name.trim()) {
      setError("Please enter your name.");
      return;
    }
    if (!contactNo.trim() || contactNo.length !== 10) {
      setError("Please enter a valid 10-digit contact number.");
      return;
    }
    if (!pincode.trim() || pincode.length !== 6) {
      setError("Please enter a valid 6-digit pincode.");
      return;
    }
    if (!state.trim()) {
      setError("Please select a state.");
      return;
    }
    if (!city.trim()) {
      setError("Please select a city.");
      return;
    }

    const reqBody = {
      Name: name,
      ContactNo: contactNo,
      PinCode: pincode,
      State: state,
      City: city,
      GSTIN: gstin.trim() || null,
    };

    try {
      setLoading(true);
      const response = await axios.post(
        global.config.APIURL.BaseURL + "api/PublicData/RequestCatalogueAccess",
        reqBody
      );
      if (response.status === 200) {
        setSubmitted(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        setError("Failed to submit your request.");
      }
    } catch (err) {
      if (err.response && err.response.data && err.response.data.Message) {
        setError(err.response.data.Message);
      } else if (err.response && typeof err.response.data === "string") {
        setError(err.response.data);
      } else {
        setError("Failed to submit your request.");
      }
    } finally {
      setLoading(false);
    }
  };

  const benefitItems = [
    { emoji: "💎", label: "Exclusive Designs" },
    { emoji: "✨", label: "Trending Collections" },
    { emoji: "📲", label: "WhatsApp Updates" },
  ];

  const stepItems = ["Fill your details", "Our team approves", "Login & explore"];

  const inputStyle = { borderRadius: 10 };

  return (
    <Spin spinning={loading} tip="Submitting...">
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: isMobile ? "flex-start" : "center",
          background:
            "linear-gradient(160deg, #fffaf3 0%, #f9edd8 45%, #f3ecff 100%)",
          padding: isMobile ? "12px 12px 32px" : "24px 16px",
        }}
      >
        <div
          style={{
            maxWidth: 460,
            width: "100%",
            margin: isMobile ? "0" : "auto",
            padding: isMobile ? "20px 16px" : "28px 30px",
            background: "#ffffff",
            borderRadius: 20,
            boxShadow: "0 16px 40px rgba(120, 84, 12, 0.14)",
            border: "1px solid #f3e3c3",
          }}
        >
          <div style={{ marginBottom: 12, textAlign: "center" }}>
            <img
              src="https://thejoj.com/images/jojlogo.png"
              alt="Jewels of Joy Logo"
              style={{ width: isMobile ? 200 : 240, height: "auto" }}
            />
          </div>

          {submitted ? (
            // ------------------- SUCCESS VIEW -------------------
            <div style={{ textAlign: "center", padding: "8px 0 4px" }}>
              <CheckCircleTwoTone
                twoToneColor="#52c41a"
                style={{ fontSize: 56, marginBottom: 12 }}
              />
              <Title level={3} style={{ color: "#389e0d", marginBottom: 4 }}>
                Request Received! 🎉
              </Title>
              <Text style={{ color: "#555", fontSize: 15 }}>
                Thank you{name ? `, ${name.split(" ")[0]}` : ""}! Our team is
                reviewing your request.
              </Text>

              <div
                style={{
                  marginTop: 20,
                  padding: "14px 16px",
                  background: "#f6ffed",
                  border: "1px solid #b7eb8f",
                  borderRadius: 12,
                  textAlign: "left",
                }}
              >
                <Text strong style={{ fontSize: 14 }}>
                  What happens next?
                </Text>
                <div style={{ marginTop: 8, fontSize: 13.5, color: "#444", lineHeight: 1.9 }}>
                  <div>
                    <WhatsAppOutlined style={{ color: "#25D366", marginRight: 8 }} />
                    You'll get a WhatsApp confirmation on{" "}
                    <Text strong>{contactNo}</Text>
                  </div>
                  <div>
                    <MobileOutlined style={{ color: GOLD, marginRight: 8 }} />
                    Then simply log in with your mobile number
                  </div>
                  <div>
                    <SafetyCertificateOutlined style={{ color: GOLD, marginRight: 8 }} />
                    And start exploring our catalogue!
                  </div>
                </div>
              </div>

              <Text
                style={{
                  display: "block",
                  marginTop: 16,
                  fontSize: 13,
                  color: "#999",
                }}
              >
                📲 Keep an eye on your WhatsApp — approval usually doesn't take
                long.
              </Text>
            </div>
          ) : (
            // ------------------- FORM VIEW -------------------
            <>
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    display: "inline-block",
                    background: "linear-gradient(135deg, #fdf3dd, #fae7c0)",
                    color: "#8a6408",
                    fontSize: 12.5,
                    fontWeight: 600,
                    padding: "4px 14px",
                    borderRadius: 999,
                    marginBottom: 10,
                  }}
                >
                  ✨ Exclusive Wholesale Catalogue
                </div>
                <Title
                  level={isMobile ? 4 : 3}
                  style={{ color: "#2d2417", margin: "0 0 6px" }}
                >
                  Get Access to Our Catalogue
                </Title>
                <Text style={{ color: "#666", fontSize: isMobile ? 13.5 : 14.5 }}>
                  Share a few details — it takes less than a minute!
                </Text>
              </div>

              {/* Benefit chips */}
              <Row gutter={8} style={{ marginTop: 16, marginBottom: 4 }}>
                {benefitItems.map((b) => (
                  <Col span={8} key={b.label}>
                    <div
                      style={{
                        background: "#fbf7ef",
                        border: "1px solid #f0e3c8",
                        borderRadius: 12,
                        textAlign: "center",
                        padding: "10px 4px",
                        height: "100%",
                      }}
                    >
                      <div style={{ fontSize: 20 }}>{b.emoji}</div>
                      <div
                        style={{
                          fontSize: 11.5,
                          color: "#6b5b3a",
                          fontWeight: 600,
                          marginTop: 2,
                          lineHeight: 1.3,
                        }}
                      >
                        {b.label}
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>

              {/* How it works strip */}
              <div style={{ margin: "24px 2px 4px" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                  }}
                >
                  {stepItems.map((label, i) => (
                    <React.Fragment key={label}>
                      <div style={{ textAlign: "center", flex: 1 }}>
                        <div
                          style={{
                            width: 26,
                            height: 26,
                            lineHeight: "26px",
                            borderRadius: "50%",
                            background:
                              i === 0
                                ? `linear-gradient(135deg, ${GOLD_LIGHT}, ${GOLD})`
                                : "#f1e8d4",
                            color: i === 0 ? "#fff" : "#9c854f",
                            fontWeight: 700,
                            fontSize: 13,
                            margin: "0 auto 6px",
                          }}
                        >
                          {i + 1}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: "#8a7a55",
                            lineHeight: 1.2,
                          }}
                        >
                          {label}
                        </div>
                      </div>
                      {i < stepItems.length - 1 && (
                        <div
                          style={{
                            flex: 0.4,
                            height: 2,
                            background: "#eadfc6",
                            borderRadius: 2,
                            marginTop: 12,
                          }}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              <Spin spinning={pincodeLoading} tip="Finding your city...">
                <Form layout="vertical" style={{ marginTop: 14 }}>
                  <Form.Item
                    label="Your Name"
                    required
                    style={{ marginBottom: isMobile ? 14 : 18 }}
                  >
                    <Input
                      prefix={<UserOutlined style={{ color: GOLD }} />}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. Priya Sharma"
                      size="large"
                      autoComplete="name"
                      style={inputStyle}
                    />
                  </Form.Item>
                  <Form.Item
                    label="WhatsApp / Mobile Number"
                    required
                    style={{ marginBottom: isMobile ? 14 : 18 }}
                  >
                    <Input
                      prefix={<MobileOutlined style={{ color: GOLD }} />}
                      value={contactNo}
                      onChange={(e) =>
                        setContactNo(e.target.value.replace(/\D/g, ""))
                      }
                      maxLength={10}
                      placeholder="10-digit mobile number"
                      size="large"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel-national"
                      style={inputStyle}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Pincode"
                    required
                    style={{ marginBottom: isMobile ? 14 : 18 }}
                    extra={
                      <span style={{ fontSize: 12 }}>
                        We'll auto-fill your state & city 🙌
                      </span>
                    }
                  >
                    <Input
                      prefix={<NumberOutlined style={{ color: GOLD }} />}
                      value={pincode}
                      maxLength={6}
                      onChange={handlePincodeChange}
                      placeholder="6-digit pincode"
                      size="large"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="postal-code"
                      style={inputStyle}
                    />
                  </Form.Item>
                  <Row gutter={10}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="State"
                        required
                        style={{ marginBottom: isMobile ? 14 : 18 }}
                      >
                        <AutoComplete
                          options={filteredStates.map((s) => ({ value: s }))}
                          value={state}
                          onSelect={handleStateSelect}
                          onSearch={handleStateSearch}
                          placeholder="Select State"
                          filterOption={false}
                          style={{ width: "100%" }}
                          size="large"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="City"
                        required
                        style={{ marginBottom: isMobile ? 14 : 18 }}
                      >
                        <AutoComplete
                          options={filteredCities.map((c) => ({ value: c }))}
                          value={city}
                          onSelect={handleCitySelect}
                          onSearch={handleCitySearch}
                          placeholder="Select City"
                          filterOption={false}
                          style={{ width: "100%" }}
                          size="large"
                          disabled={!state}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item
                    label="GST Number (Optional)"
                    style={{ marginBottom: isMobile ? 14 : 18 }}
                  >
                    <Input
                      prefix={<IdcardOutlined style={{ color: GOLD }} />}
                      value={gstin}
                      onChange={(e) => setGstin(e.target.value.toUpperCase())}
                      placeholder="Enter GST Number (if applicable)"
                      size="large"
                      maxLength={15}
                      style={inputStyle}
                    />
                  </Form.Item>

                  {error && (
                    <div style={{ marginBottom: 12 }}>
                      <Alert message={error} type="error" showIcon />
                    </div>
                  )}

                  <Button
                    type="primary"
                    block
                    size="large"
                    onClick={handleSubmit}
                    loading={loading}
                    style={{
                      height: 50,
                      borderRadius: 12,
                      fontWeight: 700,
                      fontSize: 16,
                      background: `linear-gradient(135deg, ${GOLD_LIGHT} 0%, ${GOLD} 100%)`,
                      border: "none",
                      boxShadow: "0 6px 16px rgba(184, 134, 11, 0.35)",
                    }}
                  >
                    Request Access
                  </Button>

                  <div style={{ textAlign: "center", marginTop: 14 }}>
                    <Text style={{ fontSize: 12.5, color: "#999" }}>
                      🔒 Your details are safe with us — no spam, ever.
                    </Text>
                    <div style={{ marginTop: 6 }}>
                      <Text style={{ fontSize: 13, color: "#888" }}>
                        Already have access?{" "}
                        <a
                          href="/login"
                          style={{ color: GOLD, fontWeight: 600 }}
                        >
                          Log in here
                        </a>
                      </Text>
                    </div>
                  </div>
                </Form>
              </Spin>
            </>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default RequestCatalogueAccess;
